import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {ConfigChoices, ConfigNarration, Project} from "./project";
import axios from "axios";
import {Status} from "./status";
import {headers} from "./headers";
import {Tag} from "./fields";
import {Narrator} from './Shot';
import {host} from "./api";

export interface ProjectsState {
    projects: Array<Project>,
    project: Project | undefined
    tags: Array<Tag>,
    configNarration: Array<ConfigNarration>,
    configChoice: Array<ConfigChoices>,
    narrators: Array<Narrator>
    status: Status,
    error: string | null | undefined
}

const initialState: ProjectsState = {
    projects: [],
    project: undefined,
    tags: [],
    narrators: [],
    configNarration: [
        {x: 0, y: 0, z: 0, width: 0, height: 0, background_fill_type: 'Fit', text_font_size: 32},
        {x: 0, y: 0, z: 0, width: 0, height: 0, background_fill_type: 'Fit', text_font_size: 32},
    ],
    configChoice: [
        {background_fill_type: 'Fit', text_font_size: 32},
        {background_fill_type: 'Fit', text_font_size: 32}
    ],
    status: Status.Idle,
    error: null,
}

export const resetProject = createAsyncThunk('resetProject', async () => {
    return initialState
})

export const fetchProjects = createAsyncThunk('projects', async () => {
    const response = await axios.get(`${host}/projects`, {
        headers: headers(undefined)
    })
    return response.data
})

export const fetchConfigNarration = createAsyncThunk('fetchConfigNarration', async (params: { projectId: number }) => {
    const response = await axios.get(`${host}/projects/${params.projectId}/config-narration`, {
        headers: headers(params.projectId.toString())
    })
    return response.data
})

export const fetchConfigChoice = createAsyncThunk('fetchConfigChoice', async (params: { projectId: number }) => {
    const response = await axios.get(`${host}/projects/${params.projectId}/config-choice`, {
        headers: headers(params.projectId.toString())
    })
    return response.data
})

export const fetchTags = createAsyncThunk('allTags', async (params: { projectId: number }) => {
    const response = await axios.get(`${host}/projects/${params.projectId}/tags`, {
        headers: headers(params.projectId.toString())
    })
    return response.data
})

export const fetchNarrators = createAsyncThunk('allNarrators', async (params: { projectId: number }) => {
    const response = await axios.get(`${host}/projects/${params.projectId}/narrators`, {
        headers: headers(params.projectId.toString())
    })
    return response.data
})

export const updateProject = createAsyncThunk('updateProject', async (project: Project) => {
    const response = await axios.put(`${host}/projects/${project.id}`, project, {
        headers: headers(project.id?.toString())
    })
    return response.data
})

export const saveProject = createAsyncThunk('saveProject', async (project: Project) => {
    const response = await axios.post(`${host}/projects`, project, {
        headers: headers(project.id?.toString())
    })
    return response.data
})

export const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
        setProject: (state, action) => {
            state.project = action.payload
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchProjects.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(fetchProjects.fulfilled, (state, action) => {
                state.status = Status.Idle
                state.projects = action.payload
            })
            .addCase(fetchProjects.rejected, (state, action) => {
                state.status = Status.Failed
                state.error = action.error.message
            })
            .addCase(updateProject.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(updateProject.fulfilled, (state, action) => {
                state.status = Status.Idle
                state.project = action.payload
            })
            .addCase(updateProject.rejected, (state, action) => {
                state.status = Status.Failed
                state.error = action.error.message
            })
            .addCase(saveProject.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveProject.fulfilled, (state, action) => {
                state.status = Status.Idle
                state.project = action.payload
            })
            .addCase(saveProject.rejected, (state, action) => {
                state.status = Status.Failed
                state.error = action.error.message
            })
            .addCase(fetchTags.fulfilled, (state, action) => {
                state.tags = action.payload
            })
            .addCase(fetchConfigNarration.fulfilled, (state, action) => {
                state.configNarration = action.payload
            })
            .addCase(fetchConfigChoice.fulfilled, (state, action) => {
                state.configChoice = action.payload
            })
            .addCase(fetchNarrators.fulfilled, (state, action) => {
                state.narrators = action.payload
            })
            .addCase(resetProject.fulfilled, (state, action) => {
                return action.payload
            })
    }
})

export const {setProject} = projectsSlice.actions
export default projectsSlice.reducer;